import { ReactNode } from "react";
import { NavLink, useLocation, Outlet } from "react-router-dom";
import { Button } from "@components/ui/button";
import {
  LogOut,
  LayoutDashboard,
  Users,
  Shield,
  Moon,
  Sun,
  User,
  FileText,
  FileCheck,
} from "lucide-react";
import { cn } from "@lib/utils";
import { useTheme } from "@lib/use-theme";
import { t } from "@lib/i18n";
import { useDashboardStats } from "@api/dashboard";

interface LayoutProps {
  children?: ReactNode;
  currentPage: string;
  onLogout: () => void;
}

const getMenuItems = (stats?: { total_tabs: number; total_users: number; total_artists: number; pending_submissions: number }) => [
  {
    id: "dashboard",
    path: "/dashboard",
    label: t("layout.menu.dashboard"),
    icon: LayoutDashboard,
    count: undefined,
  },
  {
    id: "artists",
    path: "/artists",
    label: t("layout.menu.artists"),
    icon: Users,
    count: stats?.total_artists,
  },
  {
    id: "users",
    path: "/users",
    label: t("layout.menu.users"),
    icon: User,
    count: stats?.total_users,
  },
  {
    id: "tabs",
    path: "/tabs",
    label: t("layout.menu.tabs"),
    icon: FileText,
    count: stats?.total_tabs,
  },
  {
    id: "tab-submissions",
    path: "/tab-submissions",
    label: t("layout.menu.tabSubmissions"),
    icon: FileCheck,
    count: stats?.pending_submissions,
  },
];

export function Layout({
  children,
  currentPage,
  onLogout,
}: LayoutProps) {
  const { theme, toggleTheme } = useTheme();
  const { data: stats } = useDashboardStats();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 border-r bg-card/50 backdrop-blur-sm flex flex-col">
        {/* Logo/Header */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-lg font-bold">{t("layout.appName")}</h1>
              <p className="text-xs text-muted-foreground">
                {t("layout.dashboard")}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {getMenuItems(stats).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path === "/dashboard" && location.pathname === "/");
            return (
              <NavLink
                key={item.id}
                to={item.path}
                className={cn(
                  "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  {item.label}
                </div>
                {item.count !== undefined && (
                  <span
                    className={cn(
                      "px-2 py-0.5 rounded-full text-xs font-semibold",
                      isActive
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {item.count.toLocaleString()}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Theme Toggle & Logout */}
        <div className="p-4 border-t space-y-2">
          <Button
            variant="outline"
            onClick={toggleTheme}
            className="w-full gap-2"
          >
            {theme === "dark" ? (
              <>
                <Sun className="w-4 h-4" />
                {t("layout.theme.lightMode")}
              </>
            ) : (
              <>
                <Moon className="w-4 h-4" />
                {t("layout.theme.darkMode")}
              </>
            )}
          </Button>
          <Button variant="outline" onClick={onLogout} className="w-full gap-2">
            <LogOut className="w-4 h-4" />
            {t("common.logout")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="container mx-auto p-8">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}
